import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import type { ICellRendererParams } from "ag-grid-community";
import styles from "./ClientDocuments.module.css";

// --- your existing exports (unchanged) ---
export type StatusClass = "completed" | "review" | "draft" | "processing" | "error" | "action-pending" | "other";

export const categorizeStatus = (raw: string | undefined | null): StatusClass => {
  const k = String(raw ?? "").trim().toLowerCase();
  if (!k) return "other";
  if (["completed","complete","verified","approved","return selected"].includes(k)) return "completed";
  if (["ready for review","ready_for_review","needs review","awaiting review","awaiting approval","ready"].includes(k)) return "review";
  if (["draft","unverified","incomplete","created"].includes(k)) return "draft";
  if (["processing","in progress","in-progress","queued","running","uploading","extracting","pending"].includes(k)) return "processing";
  if (/^select(\s+a)?\s+return$/i.test(k)) return "action-pending";
  if (k.includes("pending upload") || k.includes("awaiting") || k.includes("needs")) return "action-pending";
  if (/\b(error|failed|failure|reject|rejected)\b/i.test(k)) return "error";
  return "other";
};

export const categorizeTaxStatus = (raw: string | undefined | null): StatusClass => {
  const k = String(raw ?? "").trim();
  if (!k) return "other";
  if (/^completed?$/i.test(k)) return "completed";
  if (/^pending upload?$/i.test(k)) return "action-pending";
  if (/\bstep\b\s*[:\-]?\s*\d+\s*(?:\/|of)\s*\d+\b/i.test(k)) return "processing";
  if (/\b(error|failed|failure|reject|rejected)\b/i.test(k)) return "error";
  if (/^not\s*moved\b/i.test(k)) return "other";
  return "other";
};

// --- utils ---
const truncate = (v: unknown, n = 280) => {
  const s = typeof v === "string" ? v : v == null ? "" : JSON.stringify(v);
  const t = s.trim();
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
};

type FieldKey = "status" | "taxstatus" | undefined;
const getFieldFromParams = (p: ICellRendererParams<any, any>): FieldKey => {
  const name = String(p.colDef?.field ?? p.colDef?.colId ?? "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (name === "taxstatus") return "taxstatus";
  if (name === "status") return "status";
  return undefined;
};

// --- shared renderer ---
export const StatusCellRenderer = (p: ICellRendererParams<any, any>) => {
  const field = getFieldFromParams(p); // "status" | "taxstatus" | undefined
  const raw =
    (typeof p.value === "string" ? p.value : undefined) ??
    (field ? String(p.data?.[field === "status" ? "Status" : "TaxStatus"] ?? "") : "");

  const cls = field === "taxstatus" ? categorizeTaxStatus(raw) : categorizeStatus(raw);

  // Behaviors
  const isSelectReturn = /^select(\s+a)?\s+return$/i.test(String(raw || ""));
  const isError = cls === "error";
  const isPendingUpload = /\bpending\s+upload\b/i.test(String(raw || ""));

  // Tooltip only for error or "Select a Return"
  const showTooltip = isError || isSelectReturn;

  // Click handler for "Pending Upload" (from either params or grid context)
  const ctx: any = (p as any).context || {};
  const openUpload =
    (p.colDef as any)?.cellRendererParams?.onPendingUpload ??
    ctx.onPendingUpload ??
    ctx.openUploadDialog; // allow multiple names

  const pill = (
    <span
      className={[
        styles["client-documents-statusPill"],
        styles["client-documents-status--" + cls],
        (showTooltip || isPendingUpload) ? styles["client-documents-status--clickable"] : "",
      ].join(" ")}
      data-status-class={cls}
      data-status-field={field}
      title={showTooltip ? undefined : raw || "—"}
      aria-label={
        isPendingUpload
          ? "Pending upload – click to upload"
          : showTooltip
          ? "More info – hover for details"
          : `Status: ${raw || "—"}`
      }
      tabIndex={(showTooltip || isPendingUpload) ? 0 : -1}
      onClick={() => {
        if (isPendingUpload && typeof openUpload === "function") {
          openUpload(p.data);
        }
      }}
      onKeyDown={(e) => {
        if (isPendingUpload && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          if (typeof openUpload === "function") openUpload(p.data);
        }
      }}
      style={{ cursor: (showTooltip || isPendingUpload) ? "pointer" : "default" }}
      role={(showTooltip || isPendingUpload) ? "button" : undefined}
    >
      {raw || "—"}
    </span>
  );

  if (!showTooltip) return pill;

  // Tooltip text
  const tipText = isSelectReturn
    ? "Please select a Return Id by clicking ⬇️ in actions"
    : (() => {
        const msgRaw =
          field === "taxstatus"
            ? p.data?.TaxMessage ??
              p.data?.StatusMessage ??
              p.data?.ErrorSummary ??
              p.data?.ErrorDetail ??
              p.data?.Error ??
              p.data?.Logs
            : p.data?.StatusMessage ??
              p.data?.TaxMessage ??
              p.data?.ErrorSummary ??
              p.data?.ErrorDetail ??
              p.data?.Error ??
              p.data?.Logs;

        const normalized =
          (typeof msgRaw === "string"
            ? msgRaw.trim()
            : msgRaw == null
              ? ""
              : JSON.stringify(msgRaw).trim()) || "An error occurred.";
        return truncate(normalized, 280);
      })();

  const container = typeof window !== "undefined" ? document.body : undefined;

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 120, hide: 80 }}
      trigger={["hover", "focus"]}
      container={container}
      overlay={
        <Tooltip
          id={`status-tip-${p.data?.DocumentId ?? "x"}-${field ?? "unknown"}`}
          className={styles["client-documents-errorTooltip"]}
        >
          <div className={styles["client-documents-errorMsg"]}>{tipText}</div>
        </Tooltip>
      }
    >
      {pill}
    </OverlayTrigger>
  );
};
